<template>
	<div v-if="walkthroughData" class="walkthrough-carousel-container">
			<!-- Progress bar for story variant -->
			<div v-if="variant === 'story' && walkthroughData.slides && walkthroughData.slides.length > 1" class="progress-bar-container">
				<div class="progress-bar">
					<div 
						v-for="(slide, index) in walkthroughData.slides" 
						:key="`progress-${index}`"
						class="progress-segment"
						:class="{ active: index <= currentSlideIndex }"
					></div>
				</div>
			</div>

			<SwipeCarousel
				v-model="currentSlideIndex"
				:showArrows="false"
				:showButtons="false"
				:paginationDots="false"
				:itemCount="walkthroughData.slides.length"
				:height="height"
				@update:model-value="handleSlideChange"
			>
				<v-carousel-item v-for="(slide, index) in walkthroughData.slides" :key="`${slide.id}-${index}`">
					<MGSheetSlide
						:imageUri="slide.imageUri"
						:title="slide.title"
						:videoLoop="slide.videoLoop"
						:description="slide.description"
						:subDescription="slide.subDescription"
						:variant="variant"
						:compact="compact"
						:imageHeight="imageHeight"
						:actionUri="slide.actionUri"
					/>
				</v-carousel-item>
			</SwipeCarousel>

			<div>
				<div class="smallTextLink" v-if="currentSmallText || currentSmallLink">
					<a
						:href="currentSmallLink"
						class="slide-small-link"
						target="_blank"
					>
						* {{ currentSmallText || currentSmallLink }}
					</a>
				</div>
				<!-- Pagination dots for sheet variant -->
				<div class="pagination-container">
					<PaginationDots
						v-if="variant === 'sheet' && walkthroughData.slides && walkthroughData.slides.length > 1"
						:total-items="walkthroughData.slides.length"
						:current-index="currentSlideIndex"
						:maxDots="5"
						class="pagination-container"
					/>
				</div>

				<div class="action-button" :class="{ 'anchor-bottom-no-nav': variant === 'story' }"> 
					<MGButton
						v-if="currentActionText"
						:text="currentActionText"
						:arrow="true"
						:fullWidth="true"
						:noCaps="true"
						@click="handleActionClick"
					/>
				</div>
			</div>
			
		</div>
</template>

<script>
import MGSheetSlide from './MGSheetSlide.vue';
import PaginationDots from './PaginationDots.vue';
import SwipeCarousel from './SwipeCarousel.vue';
import MGButton from './MGButton.vue';

export default {
	name: 'MGWalkthrough',
	components: { MGSheetSlide, PaginationDots, SwipeCarousel, MGButton },
	props: {
		walkthroughData: {
			type: Object,
			required: true
		},
		variant: {
			type: String,
			default: 'sheet',
			validator: (value) => ['sheet', 'story'].includes(value)
		},
		slideKey: {
			type: String,
			default: null
		},
		height: {
			type: String,
			default: null
		},
		compact : {
			type: Boolean,
			default: false
		},
		imageHeight: {
			type: String,
			default: '220'
		}
	},
	emits: ['slide-title-changed', 'close-drawer', 'slide-index-changed', 'action-clicked'],
	data() {
		return {
			currentSlideIndex: 0,
			autoScrollTimer: null,
			preloadedMedia: []
		}
	},
	computed: {
		currentSlideTitle() {
			if (!this.walkthroughData || !this.walkthroughData.slides || this.walkthroughData.slides.length === 0) {
				return '';
			}
			return this.walkthroughData.slides[this.currentSlideIndex]?.screenTitle || '';
		},
		currentActionText() {
			if (!this.walkthroughData || !this.walkthroughData.slides || this.walkthroughData.slides.length === 0) {
				return '';
			}
			return this.walkthroughData.slides[this.currentSlideIndex]?.actionText || '';
		},
		currentActionUri() {
			if (!this.walkthroughData || !this.walkthroughData.slides || this.walkthroughData.slides.length === 0) {
				return '';
			}
			return this.walkthroughData.slides[this.currentSlideIndex]?.actionUri || '';
		},
		currentSmallText() {
			if (!this.walkthroughData || !this.walkthroughData.slides || this.walkthroughData.slides.length === 0) {
				return '';
			}
			return this.walkthroughData.slides[this.currentSlideIndex]?.smallText || '';
		},
		currentSmallLink() {
			if (!this.walkthroughData || !this.walkthroughData.slides || this.walkthroughData.slides.length === 0) {
				return '';
			}
			return this.walkthroughData.slides[this.currentSlideIndex]?.smallLink || '';
		}
	},
	watch: {
		walkthroughData: {
			handler() {
				if (this.walkthroughData && this.walkthroughData.slides) {
					const title = this.walkthroughData.slides[this.currentSlideIndex]?.screenTitle || '';
					this.$emit('slide-title-changed', title);
					if (this.walkthroughData.autoScrollSeconds > 0) {
						this.startAutoScroll();
					}
					this.preloadAllMedia();
				}
			}
		},
		slideKey: {
			handler(newSlideKey) {
				if (newSlideKey && this.walkthroughData && this.walkthroughData.slides) {
					const matchingSlideIndex = this.walkthroughData.slides.findIndex(
						slide => slide.key === newSlideKey
					);
					if (matchingSlideIndex !== -1) {
						this.currentSlideIndex = matchingSlideIndex;
					}
					else {
						this.currentSlideIndex = 0;
					}
				}
			}
		},
		currentSlideIndex: {
			handler(newIndex) {
				if (this.walkthroughData && this.walkthroughData.slides) {
					const title = this.walkthroughData.slides[newIndex]?.screenTitle || '';
					this.$emit('slide-title-changed', title);
					this.$emit('slide-index-changed', newIndex);

					// Manage video playback on slide changes
					this.$nextTick(() => {
						const videos = this.$el.querySelectorAll('video');
						videos.forEach((video, index) => {
							if (index === newIndex) {
								video.play().catch(() => {});
							} else {
								video.pause();
							}
						});
					});
				}
			}
		}
	},
	methods: {
		handleActionClick() {
			if (this.currentActionUri) {
				this.$emit('action-clicked', this.currentActionUri);
			} else if (this.currentActionText) {
				this.goToNextSlide();
			}
		},	
		startAutoScroll() {
			this.stopAutoScroll();
			if (this.walkthroughData.autoScrollSeconds > 0) {
				this.autoScrollTimer = setInterval(() => {
					this.goToNextSlide();
				}, this.walkthroughData.autoScrollSeconds * 1000);
			}
		},
		stopAutoScroll() {
			if (this.autoScrollTimer) {
				clearInterval(this.autoScrollTimer);
				this.autoScrollTimer = null;
			}
		},
		goToNextSlide() {
			if (this.walkthroughData && this.walkthroughData.slides) {
				const nextIndex = this.currentSlideIndex + 1;
				if (nextIndex < this.walkthroughData.slides.length) {
					this.currentSlideIndex = nextIndex;
				} else {
					this.currentSlideIndex = 0;
				}
			}
		},
		goToPreviousSlide() {
			if (this.currentSlideIndex > 0) {
				this.currentSlideIndex = this.currentSlideIndex - 1;
			}
		},
		handleSlideChange() {
			if (this.walkthroughData.autoScrollSeconds > 0) {
				this.startAutoScroll();
			}
		},
		preloadAllMedia() {
			if (!this.walkthroughData || !this.walkthroughData.slides) return;

			const videoExtensions = ['.mp4', '.webm', '.mov', '.avi'];

			this.walkthroughData.slides.forEach(slide => {
				if (!slide.imageUri) return;

				const isVideo = videoExtensions.some(ext =>
					slide.imageUri.toLowerCase().endsWith(ext)
				);

				if (isVideo) {
					const video = document.createElement('video');
					video.preload = 'auto';
					video.src = slide.imageUri;
					this.preloadedMedia.push(video);
				} else {
					const img = new Image();
					img.src = slide.imageUri;
					this.preloadedMedia.push(img);
				}
			});
		}
	},
	beforeUnmount() {
		this.stopAutoScroll();
	},
	mounted() {
		// Emit title on mount in case prop was already loaded
		if (this.walkthroughData && this.walkthroughData.slides) {
			// If slideKey is provided, find and set the matching slide
			if (this.slideKey) {
				const matchingSlideIndex = this.walkthroughData.slides.findIndex(
					slide => slide.key === this.slideKey
				);
				if (matchingSlideIndex !== -1) {
					this.currentSlideIndex = matchingSlideIndex;
				}
			}

			const title = this.walkthroughData.slides[this.currentSlideIndex]?.screenTitle || '';
			this.$emit('slide-title-changed', title);
			if (this.walkthroughData.autoScrollSeconds > 0) {
				this.startAutoScroll();
			}
			this.preloadAllMedia();
		}
	}
}
</script>

<style scoped>
.action-button {
	margin-left: 20px;
	margin-right: 20px;
}	
.pagination-container {
	padding-top: 10px;
	display: flex;
	justify-content: center;
}
.progress-bar-container {
	width: 100%;
	padding: 12px 12px 8px 12px;
	box-sizing: border-box;
}

.progress-bar {
	width: 100%;
	display: flex;
	gap: 4px;
}

.progress-segment {
	flex: 1;
	height: 4px;
	background-color: rgba(0, 0, 0, 0.1);
	border-radius: 2px;
	transition: background-color 0.3s ease;
}

.progress-segment.active {
	background-color: var(--background-color-alt2, #332847);
}

.pagination-container {
	margin-top: -5px;
	margin-bottom: 16px;
	display: flex;
	justify-content: center;
	margin-top: 12px;
}

.smallTextLink {
	/* margin-top: 20px; */
	margin-bottom: -23px;
	padding-left: 25px;
}
.walkthrough-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 16px;
}

.walkthrough-title {
	font-size: 18px;
	font-weight: 600;
	color: var(--text-color);
	margin: 0;
}

.walkthrough-carousel-container {
	width: 100%;
	position: relative;
}

/* Add extra spacing on smaller screens when subdescription wraps */
@media (max-height: 700px) {
	.pagination-container {
		margin-top: 24px !important;
		margin-bottom: 8px;
	}

	.smallTextLink {
		margin-top: 8px;
	}
}
</style>
